import { useEffect } from "react";
import PropTypes from "prop-types";
import { Box, DialogActions, Typography } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import ModalBasicLayout from "./ModalBasicLayout";
import TextField from "../Form/TextField";
import ContainerModalActions from "../Containers/ContainerModalActions";
import {
  FEATURE_ADDON_BILLING_CANCEL_DESCRIPTION,
  FEATURE_ADDON_BILLING_CANCEL_MODAL_CONFIRM,
  FEATURE_ADDON_BILLING_CANCEL_MODAL_KEEP,
  FEATURE_ADDON_BILLING_CANCEL_MODAL_TITLE,
  MODAL_CANCEL_SUBSCRIPTION_CONFIRM_WORD,
  MODAL_CANCEL_SUBSCRIPTION_DESTRUCTIVE_CONFIRM_PROMPT,
} from "../../i18n/keysTranslations";

const confirmPromptComponents = {
  boldDanger: (
    <Box component="strong" sx={{ fontWeight: 700, color: "error.main" }} />
  ),
};

const ModalCancelFeatureAddon = ({
  modalOpen,
  onCloseModal,
  onConfirm,
  loading = false,
  renewDate,
  description = "",
}) => {
  const { t } = useTranslation();
  const confirmWord = t(MODAL_CANCEL_SUBSCRIPTION_CONFIRM_WORD);
  const { control, watch, reset } = useForm({
    defaultValues: { destructiveConfirm: "" },
  });
  const typed = watch("destructiveConfirm") || "";
  const canConfirm = typed.trim() === confirmWord;
  const endDate = renewDate ? new Date(Number(renewDate)) : new Date();

  useEffect(() => {
    if (!modalOpen) reset();
  }, [modalOpen, reset]);

  const requestClose = () => {
    if (loading) return;
    onCloseModal();
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={requestClose}
      title={t(FEATURE_ADDON_BILLING_CANCEL_MODAL_TITLE)}
      description=""
      maxWidth="xs"
      fullWidth
      showCloseButton={!loading}
    >
      <Box sx={{ width: "100%", maxWidth: 600, alignSelf: "center" }}>
        <Typography variant="body1" sx={{ lineHeight: 1.5, mb: 2.5 }}>
          {description ||
            t(FEATURE_ADDON_BILLING_CANCEL_DESCRIPTION, { date: endDate })}
        </Typography>
        <Typography
          variant="body1"
          component="div"
          sx={{ color: "text.primary", mb: 1 }}
        >
          <Trans
            i18nKey={MODAL_CANCEL_SUBSCRIPTION_DESTRUCTIVE_CONFIRM_PROMPT}
            components={confirmPromptComponents}
          />
        </Typography>
        <TextField
          control={control}
          name="destructiveConfirm"
          variant="filled"
          fullWidth
          rules={{}}
          errors={{}}
          autoComplete="off"
        />
      </Box>
      <DialogActions sx={{ p: 0 }}>
        <ContainerModalActions
          hasPadding
          sx={{
            flexDirection: "column",
            gap: 1,
          }}
          propsPrimaryButton={{
            showButton: true,
            disabled: loading,
            text: FEATURE_ADDON_BILLING_CANCEL_MODAL_KEEP,
            onClick: requestClose,
          }}
          propsSecondaryButton={{
            showButton: true,
            disabled: !canConfirm || loading,
            loading,
            text: FEATURE_ADDON_BILLING_CANCEL_MODAL_CONFIRM,
            onClick: onConfirm,
          }}
        />
      </DialogActions>
    </ModalBasicLayout>
  );
};

ModalCancelFeatureAddon.propTypes = {
  modalOpen: PropTypes.bool.isRequired,
  onCloseModal: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  renewDate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  description: PropTypes.string,
};

export default ModalCancelFeatureAddon;
